import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { LOGO, HONORIFICS, US_STATES, COUNTRIES } from "@/lib/brand";
import { formatUSPhone, isValidUSPhone } from "@/lib/phone";

const IMG = "https://images.unsplash.com/photo-1552174588-6733961c358e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxyYWRpbyUyMGhvc3QlMjBtaWNyb3Bob25lJTIwZGFyayUyMHN0dWRpb3xlbnwwfHx8fDE3ODc5Njk4OTh8MA&ixlib=rb-4.1.0&q=85";
const selCls = "mt-1.5 w-full h-12 rounded-md bg-[#1E2A5A] border border-white/10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("United States");
  const [smsOptin, setSmsOptin] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    if (!isValidUSPhone(phone)) {
      setError("Please enter a valid US phone number, e.g. (555) 123-4567");
      setLoading(false);
      return;
    }
    try {
      await register({ name, email, password, title, phone, birth_date: birthDate, city, state, country, sms_optin: smsOptin });
      toast.success("Welcome to the family! 🙌 Add a profile picture or pick a gospel avatar below.", { duration: 6000 });
      navigate("/profile");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A1030]">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO} alt="In His Image Radio" className="w-11 h-11 rounded-2xl object-cover" />
            <span className="font-display font-extrabold text-xl tracking-tight">In His Image Radio</span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-1">Join the family</h1>
          <p className="text-zinc-400 mb-8">Create your free listener account.</p>

          {error && <div className="mb-4 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-4 py-3" data-testid="register-error">{error}</div>}

          <div className="space-y-4">
            <div>
              <label className="overline text-zinc-400">Title</label>
              <input list="honorifics" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Pastor, Sister, Dr…" data-testid="register-title" className="mt-1.5 w-full h-12 rounded-md bg-[#1E2A5A] border border-white/10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
              <datalist id="honorifics">{HONORIFICS.map((h) => <option key={h} value={h} />)}</datalist>
            </div>
            <div><label className="overline text-zinc-400">Name</label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
            <div><label className="overline text-zinc-400">Email</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
            <div><label className="overline text-zinc-400">Phone</label><Input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(formatUSPhone(e.target.value))} placeholder="(555) 123-4567" required data-testid="register-phone" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
            <div><label className="overline text-zinc-400">Password</label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <label className="overline text-zinc-400">Birth date</label>
                <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} data-testid="register-birthdate" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12 w-full min-w-0" />
              </div>
              <div className="min-w-0"><label className="overline text-zinc-400">City</label><Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="register-city" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12 w-full min-w-0" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <label className="overline text-zinc-400">State</label>
                <select value={state} onChange={(e) => setState(e.target.value)} data-testid="register-state" className={selCls}>
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="min-w-0">
                <label className="overline text-zinc-400">Country</label>
                <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="register-country" className={selCls}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          <label className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-[#141C42] border border-white/10 cursor-pointer select-none" data-testid="register-sms-optin-label">
            <input type="checkbox" checked={smsOptin} onChange={(e) => setSmsOptin(e.target.checked)} data-testid="register-sms-optin" className="mt-0.5 w-5 h-5 shrink-0 accent-[#E6B34A] cursor-pointer" />
            <span className="text-xs leading-relaxed text-zinc-400">
              <span className="text-zinc-200 font-semibold">Yes, text me!</span> I agree to receive recurring automated marketing &amp; informational text messages (devotionals, show &amp; event reminders) from In His Image Radio at the number provided. Consent is not a condition of any purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply <span className="font-semibold text-zinc-300">STOP</span> to unsubscribe or <span className="font-semibold text-zinc-300">HELP</span> for help. See our <Link to="/terms" className="text-[#E6B34A] hover:underline">Terms</Link> &amp; <Link to="/privacy" className="text-[#E6B34A] hover:underline">Privacy Policy</Link>.
            </span>
          </label>

          <button type="submit" disabled={loading} data-testid="register-submit" className="w-full mt-6 h-12 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
          </button>
          <p className="text-xs text-zinc-500 mt-3 text-center">After signing up you can add a profile photo or pick a gospel avatar. 📷</p>

          <p className="text-sm text-zinc-400 mt-6 text-center">Already a member? <Link to="/login" className="text-[#E6B34A] font-semibold" data-testid="to-login">Sign in</Link></p>
        </form>
      </div>

      <div className="relative hidden lg:block grain overflow-hidden order-1 lg:order-2">
        <img src={IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1030] via-[#0A1030]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <p className="overline text-[#E6B34A] mb-3">In His Image Radio · Gospel</p>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-tight max-w-md">Your soundtrack for faith.</h2>
          <p className="text-zinc-400 mt-3">Free forever. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
}
